import { useContext, useState, useEffect, useCallback } from 'react'
import { AuthContext } from 'contexts'
import { eventManager } from 'utils'
import { TARGET_USERS, MODAL_CONFIG, STORAGE_KEYS } from 'constants/passwordReminder'
import type { UsePasswordReminderReturn } from 'modals/PasswordReminderModal/PasswordReminderModal.types'

export const usePasswordReminder = (): UsePasswordReminderReturn => {
  const { user } = useContext(AuthContext)
  const [hasUpdatedPassword, setHasUpdatedPassword] = useState<boolean>(false)
  const [lastShownDate, setLastShownDate] = useState<string | null>(null)

  // Check if current user is in target list
  const isTargetUser = useCallback((): boolean => {
    if (!user?.iressLoginId) return false
    return TARGET_USERS.includes(user.iressLoginId as any)
  }, [user?.iressLoginId])

  // Check if password meets new requirements (14+ characters)
  const hasValidPassword = useCallback((): boolean => {
    if (!user?.iressPassword) return false
    return user.iressPassword.length >= 14
  }, [user?.iressPassword])

  // Check if modal should be shown today (once per day maximum)
  const shouldShowToday = useCallback((): boolean => {
    const today = new Date().toDateString()
    return lastShownDate !== today
  }, [lastShownDate])

  // Load last shown date from localStorage
  useEffect(() => {
    try {
      const lastShown = localStorage.getItem(STORAGE_KEYS.PASSWORD_REMINDER_LAST_SHOWN)
      setLastShownDate(lastShown)
      setHasUpdatedPassword(hasValidPassword())
    } catch (error) {
      console.warn('Failed to read password reminder last shown date:', error)
      setLastShownDate(null)
      setHasUpdatedPassword(hasValidPassword())
    }
  }, [hasValidPassword])

  // Update state when user password changes
  useEffect(() => {
    setHasUpdatedPassword(hasValidPassword())
  }, [hasValidPassword])

  // Determine if modal should be shown
  const shouldShowModal = isTargetUser() && !hasUpdatedPassword && shouldShowToday()

  // Mark that modal was shown today
  const markShownToday = useCallback((): void => {
    try {
      const today = new Date().toDateString()
      localStorage.setItem(STORAGE_KEYS.PASSWORD_REMINDER_LAST_SHOWN, today)
      setLastShownDate(today)
    } catch (error) {
      console.error('Failed to mark password reminder as shown:', error)
    }
  }, [])

  // Open external URL securely
  const handleGoToUpdate = useCallback((): void => {
    try {
      const newWindow = window.open(MODAL_CONFIG.externalUrl, '_blank', 'noopener,noreferrer')
      if (!newWindow) {
        // Fallback if popup is blocked
        console.warn('Popup blocked. URL:', MODAL_CONFIG.externalUrl)
        // Could show a fallback modal with copy-to-clipboard functionality
      }
    } catch (error) {
      console.error('Failed to open external URL:', error)
    }
  }, [])

  // Handle remind me later (just close modal)
  const handleRemindLater = useCallback((): void => {
    // Mark that we've shown the modal today (prevents re-showing today)
    markShownToday()
    eventManager.closePasswordReminderModal()
  }, [markShownToday])

  // Open modal with content and callbacks
  const openModal = useCallback((): void => {
    if (!shouldShowModal) return

    // Mark that we're showing the modal today
    markShownToday()

    eventManager.openPasswordReminderModal(
      {
        title: MODAL_CONFIG.title,
        message: MODAL_CONFIG.message,
        externalUrl: MODAL_CONFIG.externalUrl,
        passwordRequirements: MODAL_CONFIG.passwordRequirements,
        supportEmail: MODAL_CONFIG.supportEmail,
      },
      {
        onGoToUpdate: handleGoToUpdate,
        onRemindLater: handleRemindLater,
      }
    )
  }, [shouldShowModal, handleGoToUpdate, handleRemindLater, markShownToday])

  return {
    shouldShowModal,
    openModal,
    markCompleted: markShownToday, // Keep for backward compatibility
    isTargetUser: isTargetUser(),
    hasCompleted: hasUpdatedPassword, // Now reflects password status
  }
}

export default usePasswordReminder